/**
 * 商城页面 - 普通用户浏览和购买商品
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { productApi, type Product, type ProductCategory } from '@/services/productApi';
import { Search, ShoppingBag, Loader2, Tag, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export function MallPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');

  // 加载分类列表
  useEffect(() => {
    loadCategories();
  }, []);

  // 加载商品列表
  useEffect(() => {
    loadProducts();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const data = await productApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('加载分类失败:', error);
      toast.error('加载分类失败');
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await productApi.getProducts({
        category: selectedCategory || undefined,
        page: 0,
        size: 50,
        status: 1, // 只显示上架的商品
      });
      setProducts(data.list);
    } catch (error) {
      console.error('加载商品失败:', error);
      toast.error('加载商品失败');
    } finally {
      setLoading(false);
    }
  };

  // 过滤商品
  const filteredProducts = products.filter((product) => {
    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      product.name.toLowerCase().includes(keyword) ||
      product.subtitle?.toLowerCase().includes(keyword) ||
      product.description?.toLowerCase().includes(keyword)
    );
  });

  // 跳转到购买链接
  const handlePurchase = (product: Product) => {
    if (!product.purchaseUrl) {
      toast.error('该商品暂未配置购买链接');
      return;
    }
    window.open(product.purchaseUrl, '_blank');
  };

  // 跳转到商品详情
  const handleViewDetail = (product: Product) => {
    navigate(`/mall/product/${product.productId}`);
  };

  return (
    <LayoutWithSidebar title="商城">
      <div className="min-h-screen bg-gray-50">
        {/* 页头 - 移动端优化 */}
        <div className="bg-white border-b safe-top">
          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">商城</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1 truncate">浏览和购买智能硬件产品</p>
              </div>
              <ShoppingBag className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {/* 侧边栏 - 分类 - 移动端优化为横向滚动 */}
            <div className="md:col-span-1 order-2 md:order-1">
              {/* 移动端：横向滚动的分类标签 */}
              <div className="md:hidden mb-4">
                <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors touch-target ${
                      !selectedCategory
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    全部商品
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.code}
                      onClick={() => setSelectedCategory(category.code)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors touch-target ${
                        selectedCategory === category.code
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* 桌面端：传统的垂直分类卡片 */}
              <Card className="hidden md:block">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Tag className="h-5 w-5" />
                    商品分类
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors text-sm sm:text-base touch-target ${
                      !selectedCategory
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    全部商品
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.code}
                      onClick={() => setSelectedCategory(category.code)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors text-sm sm:text-base touch-target ${
                        selectedCategory === category.code
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* 主内容 - 商品列表 */}
            <div className="md:col-span-3 order-1 md:order-2">
              {/* 搜索框 - 移动端优化 */}
              <div className="mb-4 sm:mb-6 sticky top-0 z-10 bg-gray-50 pt-2 sm:pt-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜索商品名称..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 商品列表 - 移动端优化网格 */}
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-3 text-gray-600 text-sm sm:text-base">加载中...</span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <Alert>
                  <AlertDescription className="text-sm sm:text-base">
                    {searchKeyword ? '未找到匹配的商品' : '暂无商品'}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                  {filteredProducts.map((product) => (
                    <Card
                      key={product.productId}
                      className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleViewDetail(product)}
                    >
                      {/* 商品图片 - 移动端优化宽高比 */}
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        {product.coverImageUrl ? (
                          <img
                            src={product.coverImageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingBag className="h-12 w-12 sm:h-16 sm:w-16" />
                          </div>
                        )}
                        {product.originalPrice && product.originalPrice > product.price && (
                          <Badge className="absolute top-2 right-2 bg-red-500 text-xs px-2 py-0.5">
                            {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
                          </Badge>
                        )}
                      </div>

                      <CardHeader className="pb-2 sm:pb-6 px-3 sm:px-6 pt-3 sm:pt-6">
                        <CardTitle className="text-base sm:text-lg line-clamp-1">{product.name}</CardTitle>
                        {product.subtitle && (
                          <CardDescription className="line-clamp-2 text-xs sm:text-sm">{product.subtitle}</CardDescription>
                        )}
                      </CardHeader>

                      <CardContent className="pb-2 sm:py-4 px-3 sm:px-6">
                        <div className="space-y-1 sm:space-y-2">
                          {/* 价格 - 移动端优化字体大小 */}
                          <div className="flex items-baseline gap-1 sm:gap-2">
                            <span className="text-lg sm:text-2xl font-bold text-red-600">
                              ¥{product.price.toFixed(2)}
                            </span>
                            {product.originalPrice && (
                              <span className="text-xs sm:text-sm text-gray-400 line-through">
                                ¥{product.originalPrice.toFixed(2)}
                              </span>
                            )}
                          </div>

                          {/* 规格 - 移动端隐藏次要信息 */}
                          {product.specs && Object.keys(product.specs).length > 0 && (
                            <div className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1 hidden sm:block">
                              {Object.entries(product.specs).slice(0, 3).map(([key, value]) => (
                                <div key={key}>
                                  <span className="font-medium">{key}:</span> {value}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>

                      <CardFooter className="px-3 sm:px-6 pb-3 sm:pt-4">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePurchase(product);
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700 h-9 sm:h-auto text-xs sm:text-sm"
                          size="sm"
                        >
                          <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">立即购买</span>
                          <span className="sm:hidden">购买</span>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutWithSidebar>
  );
}
